"use client";

import { Container } from "@/components/ui/container";
import { Reveal } from "@/components/ui/reveal";
import { Icon } from "@/components/ui/icon";
import { challenge } from "@/lib/data/homepage";

/**
 * The Challenge — split layout. Left: dramatic oversized statement
 * with all 4 challenges stacked. Right: custom data visualization
 * (2.5 quintillion bytes, 6000+ messages, 8-sec attention, 6x faster).
 */
export function Challenge() {
  return (
    <section id="challenge" className="relative section-pad">
      <Container>
        <div className="grid lg:grid-cols-[1.1fr_0.9fr] gap-12 items-start">
          {/* LEFT — dramatic statement + challenge cards */}
          <Reveal>
            <div className="space-y-10">
              <div>
                <span className="inline-flex items-center gap-2 px-3 py-1 rounded-full glass eyebrow-tiny text-[var(--color-crimson)]">
                  <span className="h-1.5 w-1.5 rounded-full bg-[var(--color-crimson)]" />
                  The Challenge
                </span>
                <h2>
                  {challenge.title}
                </h2>
                <p className="mt-5 text-[1.1rem] leading-[1.7] text-[rgb(var(--fg-muted))] max-w-xl">
                  {challenge.intro}
                </p>
              </div>

              <div className="space-y-3">
                {challenge.cards.map((c, i) => (
                  <div
                    key={c.title}
                    className="group rounded-2xl border border-[rgb(var(--border)/0.18)] bg-[rgb(var(--surface)/0.4)] hover:bg-[rgb(var(--surface)/0.7)] p-5 transition-all"
                  >
                    <div className="flex items-start gap-4">
                      <span
                        className="grid place-items-center h-11 w-11 rounded-xl shrink-0 transition-transform group-hover:scale-110"
                        style={{
                          background: "linear-gradient(135deg, rgb(220 20 60 / 0.18), rgb(220 20 60 / 0.06))",
                          boxShadow: "inset 0 0 0 1px rgb(220 20 60 / 0.3)",
                        }}
                      >
                        <Icon name={c.icon} className="h-5 w-5 text-[var(--color-crimson)]" />
                      </span>
                      <div className="flex-1 min-w-0">
                        <h3>{c.title}</h3>
                        <p className="mt-1.5 text-body-sm leading-relaxed text-[rgb(var(--fg-muted))]">
                          {c.desc}
                        </p>
                        {/* Intensity bar */}
                        <div className="mt-3 h-1.5 rounded-full bg-[rgb(var(--border)/0.15)] overflow-hidden">
                          <div
                            className="h-full rounded-full transition-all duration-1000"
                            style={{
                              width: `${c.intensity}%`,
                              background: "linear-gradient(90deg, var(--color-amber), var(--color-crimson))",
                            }}
                          />
                        </div>
                        <div className="mt-1.5 flex items-center justify-between eyebrow-tiny text-[rgb(var(--fg-subtle))] font-semibold">
                          <span>Threat Intensity</span>
                          <span className="font-mono-num text-[var(--color-crimson)]">{c.intensity}%</span>
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </Reveal>

          {/* RIGHT — custom data visualization panel */}
          <Reveal delay={120} className="lg:sticky lg:top-24">
            <div className="rounded-3xl glass p-8 relative overflow-hidden">
              <div className="absolute -top-20 -right-20 h-64 w-64 rounded-full bg-[var(--color-crimson)] opacity-15 blur-3xl pointer-events-none" />

              <div className="eyebrow-tiny text-[rgb(var(--fg-subtle))] mb-2">
                The Data, Unfiltered
              </div>
              <h3 className="mb-8">
                The numbers don't lie.
              </h3>

              <div className="space-y-6">
                <Stat
                  label="Bytes created daily"
                  value="2.5"
                  unit="quintillion"
                  bar={98}
                  color="var(--color-crimson)"
                />
                <Stat
                  label="Brand messages before lunch"
                  value="6,000–10,000"
                  unit="per person"
                  bar={92}
                  color="var(--color-coral)"
                />
                <Stat
                  label="Average attention span"
                  value="< 8"
                  unit="seconds"
                  bar={86}
                  color="var(--color-amber)"
                />
                <Stat
                  label="Misinformation spread rate"
                  value="6×"
                  unit="faster than truth"
                  bar={94}
                  color="var(--color-purple)"
                />
                <Stat
                  label="Crisis response window"
                  value="24–72"
                  unit="hours"
                  bar={88}
                  color="var(--color-emerald)"
                />
                <Stat
                  label="Consumers research before engaging"
                  value="87%"
                  unit="of all decisions"
                  bar={87}
                  color="var(--color-cyan)"
                />
              </div>

              <div className="mt-8 pt-6 border-t border-[rgb(var(--border)/0.15)]">
                <p className="text-body-xs leading-relaxed text-[rgb(var(--fg-muted))] italic">
                  In this environment, your narrative either <span className="text-[var(--color-crimson)] font-bold not-italic">dominates</span> or <span className="line-through opacity-60">disappears</span>.
                </p>
              </div>
            </div>
          </Reveal>
        </div>
      </Container>
    </section>
  );
}

function Stat({ label, value, unit, bar, color }: { label: string; value: string; unit: string; bar: number; color: string }) {
  return (
    <div>
      <div className="flex items-baseline justify-between mb-1.5">
        <div className="eyebrow-tiny text-[rgb(var(--fg-subtle))]">
          {label}
        </div>
        <div className="font-mono-num font-bold text-lg" style={{ color }}>
          {value}
        </div>
      </div>
      <div className="h-1.5 rounded-full bg-[rgb(var(--border)/0.15)] overflow-hidden">
        <div
          className="h-full rounded-full transition-all duration-1000"
          style={{
            width: `${bar}%`,
            background: `linear-gradient(90deg, ${color}, color-mix(in oklch, ${color} 50%, var(--color-purple)))`,
          }}
        />
      </div>
      <div className="eyebrow-tiny text-[rgb(var(--fg-subtle))] mt-1">{unit}</div>
    </div>
  );
}
